import { Preferences } from "@capacitor/preferences";

const STORAGE_KEYS = {
  operatorID: "operatorID",
  sessionToken: "sessionToken",
  expiresAt: "expiresAt",
};

const getLocalValue = (key) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch (error) {
    console.error(`[persistentStorage] local read failed for ${key}:`, error);
    return null;
  }
};

const setLocalValue = (key, value) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, value ?? "");
  } catch (error) {
    console.error(`[persistentStorage] local write failed for ${key}:`, error);
  }
};

const removeLocalValue = (key) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error(`[persistentStorage] local remove failed for ${key}:`, error);
  }
};

export const getStoredValue = async (key) => {
  try {
    const { value } = await Preferences.get({ key });
    if (value !== null && value !== undefined) {
      return value;
    }
  } catch (error) {
    console.error(`[persistentStorage] Preferences read failed for ${key}:`, error);
  }

  return getLocalValue(key);
};

export const setStoredValue = async (key, value) => {
  const stringValue = value ?? "";
  try {
    await Preferences.set({ key, value: stringValue });
  } catch (error) {
    console.error(`[persistentStorage] Preferences write failed for ${key}:`, error);
  }
  setLocalValue(key, stringValue);
};

export const removeStoredValue = async (key) => {
  try {
    await Preferences.remove({ key });
  } catch (error) {
    console.error(`[persistentStorage] Preferences remove failed for ${key}:`, error);
  }
  removeLocalValue(key);
};

export const clearStoredValues = async () => {
  try {
    await Preferences.clear();
  } catch (error) {
    console.error("[persistentStorage] Preferences clear failed:", error);
  }
  removeLocalValue(STORAGE_KEYS.operatorID);
  removeLocalValue(STORAGE_KEYS.sessionToken);
  removeLocalValue(STORAGE_KEYS.expiresAt);
};

export const persistAuthSession = async ({ operatorID, sessionToken, expiresAt }) => {
  await Promise.all([
    setStoredValue(STORAGE_KEYS.operatorID, operatorID || ""),
    setStoredValue(STORAGE_KEYS.sessionToken, sessionToken || ""),
    setStoredValue(STORAGE_KEYS.expiresAt, expiresAt || ""),
  ]);
};

export const removeAuthSession = async () => {
  await Promise.all([
    removeStoredValue(STORAGE_KEYS.operatorID),
    removeStoredValue(STORAGE_KEYS.sessionToken),
    removeStoredValue(STORAGE_KEYS.expiresAt),
  ]);
};

export const restoreAuthSession = async () => {
  const operatorID = await getStoredValue(STORAGE_KEYS.operatorID);
  const sessionToken = await getStoredValue(STORAGE_KEYS.sessionToken);
  const expiresAt = await getStoredValue(STORAGE_KEYS.expiresAt);
  return { operatorID, sessionToken, expiresAt };
};
